import { useMemo, useState } from "react"
import { Box, Button, FormControl, InputLabel, MenuItem, Select, Stack, TextField, Typography } from "@mui/material"
import { DataGridPro } from "@mui/x-data-grid-pro"
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { useGetFlaggedPlayersTOHQuery } from "../../../core/api/toh-flagged-players-api"
import TohFlaggedPlayerActionsCell from "./TohFlaggedPlayerActionsCell"

const columns = [
    {
        field: "createdAt",
        headerName: "Flagged At",
        type: "dateTime",
        width: 180,
        sortable: false,
        valueGetter: (value) => value ? new Date(value) : null
    },
    {
        field: "playerId",
        headerName: "Player ID",
        width: 220,
        sortable: false
    },
    {
        field: "severity",
        headerName: "Severity",
        type: "number",
        width: 90,
        sortable: false
    },
    {
        field: "reason",
        headerName: "Reason",
        flex: 1,
        sortable: false,
        valueGetter: (value) => value ?? "—"
    },
    {
        field: "actions",
        headerName: "Status / Actions",
        width: 320,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => <TohFlaggedPlayerActionsCell playerId={row.playerId}/>
    }
]

const TohFlaggedPlayers = () => {
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 })
    const [from, setFrom] = useState(null)
    const [to, setTo] = useState(null)
    const [playerId, setPlayerId] = useState("")
    const [severity, setSeverity] = useState("")

    const queryArgs = useMemo(() => ({
        page: paginationModel.page,
        pageSize: paginationModel.pageSize,
        from: from ? from.startOf("day").toISOString() : undefined,
        to: to ? to.endOf("day").toISOString() : undefined,
        playerId,
        severity: typeof severity === "number" ? severity : undefined
    }), [paginationModel, from, to, playerId, severity])

    const { data, isLoading, isFetching } = useGetFlaggedPlayersTOHQuery(queryArgs)

    const handleClear = () => {
        setFrom(null)
        setTo(null)
        setPlayerId("")
        setSeverity("")
        setPaginationModel(m => ({ ...m, page: 0 }))
    }

    return (
        <>
            <Typography variant="h6">Flagged Players</Typography>
            <Stack direction="row" spacing={2} sx={{ width: "100%" }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                        label="From"
                        value={from}
                        onChange={(newValue) => {
                            setFrom(newValue)
                            setPaginationModel(m => ({ ...m, page: 0 }))
                        }}
                        format="DD/MM/YYYY"
                        slotProps={{ textField: { size: "small", sx: { width: 220 } } }}
                    />
                    <DatePicker
                        label="To"
                        value={to}
                        onChange={(newValue) => {
                            setTo(newValue)
                            setPaginationModel(m => ({ ...m, page: 0 }))
                        }}
                        format="DD/MM/YYYY"
                        slotProps={{ textField: { size: "small", sx: { width: 220 } } }}
                    />
                </LocalizationProvider>
                <TextField
                    label="Player ID"
                    size="small"
                    value={playerId}
                    onChange={(e) => {
                        setPlayerId(e.target.value)
                        setPaginationModel(m => ({ ...m, page: 0 }))
                    }}
                    sx={{ width: 260 }}
                />
                <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Severity</InputLabel>
                    <Select
                        label="Severity"
                        value={severity}
                        onChange={(e) => {
                            const v = e.target.value
                            setSeverity(v === "" ? "" : Number(v))
                            setPaginationModel(m => ({ ...m, page: 0 }))
                        }}
                    >
                        <MenuItem value=""><em>Any</em></MenuItem>
                        <MenuItem value={1}>1</MenuItem>
                        <MenuItem value={2}>2</MenuItem>
                        <MenuItem value={3}>3</MenuItem>
                        <MenuItem value={4}>4</MenuItem>
                    </Select>
                </FormControl>
                <Button variant="outlined" size="small" onClick={handleClear}>
                    Clear
                </Button>
            </Stack>
            <DataGridPro
                sx={{ width: "100%", flex: 1, minHeight: 0 }}
                disableColumnMenu
                disableColumnSorting
                disableRowSelectionOnClick
                pagination
                paginationMode="server"
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                loading={isLoading || isFetching}
                columns={columns}
                rows={data?.content ?? []}
                rowCount={data?.totalElements ?? 0}
                pageSizeOptions={[10, 25, 50]}
                getRowHeight={() => "auto"}
            />
        </>
    )
}

export default TohFlaggedPlayers
